import axios from "axios"
export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE,
  withCredentials: true,
  withXSRFToken: true,
})

export default {
  getPlan(id) {
    return apiClient.get("/plan/" + id)
  },
  getPlanPdf(token) {
    return apiClient.get("/plan/" + token + "/pdf", {
      responseType: "blob",
    })
  },
  deletePlan(token) {
    return apiClient.delete("/plan/" + token)
  },
  getPlans(params) {
    return apiClient.get("/plan", { params })
  },
  getPlanContent(token, keys) {
    return apiClient.get(
      "/plan-content?plan_token=" + token + "&" + keys.map(key => "keys[]=" + key).join("&"),
    )
  },
  createPlanContent(data) {
    /**
     * data format:
     * data.reset: boolean - should the data here replace existing data for this plan, or just append to it?
     * data.content: Array of {key: string, value: string}
     */
    return apiClient.post("/plan-content", data)
  },
  deletePlanContent(id) {
    return apiClient.delete("/plan-content/" + id)
  },
  createPlan(data) {
    return apiClient.post("/plan", data)
  },
  updatePlan(token, data) {
    return apiClient.put("/plan/" + token, data)
  },
  createPlanUser(data) {
    return apiClient.post("/plan-user", data)
  },
  register(data) {
    return apiClient.post("/auth/register", data)
  },
  registerPasswordless(data) {
    return apiClient.post("/auth/register-passwordless", data)
  },
  updateUser(id, data) {
    return apiClient.put("/user/" + id, data)
  },
  forgotPassword(data) {
    return apiClient.post("/auth/forgot-password", data)
  },
  resetPassword(data) {
    return apiClient.post("/auth/reset-password", data)
  },
  login(data) {
    return apiClient.post("/auth/login", data)
  },
  logout() {
    return apiClient.post("/auth/logout")
  },
  getUser() {
    return apiClient.get("/user")
  },
  createMedia(file, planToken) {
    const formData = new FormData()
    const ext = file.type.includes("webm")
      ? "webm"
      : file.type.includes("ogg")
        ? "ogg"
        : file.type.includes("mp4") || file.type.includes("m4a")
          ? "m4a"
          : "wav"
    formData.append("file", file, `audio.${ext}`)
    formData.append("plan_token", planToken)
    return apiClient.post("/media", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    })
  },
  getMedia(id) {
    return apiClient.get("/media/" + id)
  },
  deleteMedia(id) {
    return apiClient.delete("/media/" + id)
  },
  getUsers(params) {
    return apiClient.get("/user/all", { params })
  },
  getPlanUsers(token) {
    return apiClient.get("/plan-user?token=" + token)
  },

  // Easy Flow - Checklist endpoints
  getChecklist(token) {
    return apiClient.get(`/plan/${token}/checklist`)
  },
  progressChecklist(token) {
    return apiClient.post(`/plan/${token}/checklist/progress`)
  },
  completeChecklist(token, emailData) {
    return apiClient.post(`/plan/${token}/checklist/complete`, emailData)
  },

  // Easy Flow - Guide selection
  chooseSelfAsGuide(token) {
    return apiClient.post(`/plan/${token}/guide/self`)
  },
  inviteGuide(token, data) {
    return apiClient.post(`/plan/${token}/guide/invite`, data)
  },
  resendGuideInvite(token, data) {
    return apiClient.post(`/plan/${token}/guide/invite`, data)
  },
  cancelGuideInvite(token) {
    return apiClient.post(`/plan/${token}/guide/cancel`)
  },

  // Easy Flow - Student invitation
  inviteStudent(token, { first_name, last_name, email }) {
    return apiClient.post(`/plan/${token}/student/invite`, { first_name, last_name, email })
  },
  enterResponsesMyself(token) {
    return apiClient.post(`/plan/${token}/student/enter-responses-myself`)
  },
  guardianLetter(token) {
    return apiClient.get(`/plan/${token}/guardian-letter`, { responseType: "blob" })
  },

  // Easy Flow - Mentor management
  addMentors(token, mentors) {
    return apiClient.post(`/plan/${token}/mentors/add`, { mentors })
  },
  updateMentor(token, userId, mentorData) {
    return apiClient.put(`/plan/${token}/mentors/${userId}`, {
      mentors: [mentorData],
    })
  },
  removeMentor(token, userId) {
    return apiClient.delete(`/plan/${token}/mentors/${userId}`)
  },
  inviteMentors(token, data) {
    return apiClient.post(`/plan/${token}/mentors/invite`, data)
  },
  inviteMentor(token, userId) {
    return apiClient.post(`/plan/${token}/mentor/invite`, { user_id: userId })
  },
  getMentors(token) {
    return apiClient.get(`/plan/${token}/mentors`)
  },

  // Easy Flow - Invitation management
  getInvitations(token) {
    return apiClient.get(`/plan/${token}/invitations`)
  },
  getInvitationDetails(token) {
    return apiClient.get(`/plan/${token}/invitation-details`)
  },
  acceptInvitation(token, data = {}) {
    return apiClient.put(`/plan/${token}/invitations/accept`, data)
  },
  declineInvitation(token) {
    return apiClient.put(`/plan/${token}/invitations/decline`)
  },
  removeUser(token, userId) {
    return apiClient.delete(`/plan/${token}/users/${userId}`)
  },

  // Admin user endpoints
  adminCreateUser(data) {
    return apiClient.post("/admin/users", data)
  },
  adminUpdateUser(id, data) {
    return apiClient.patch(`/admin/users/${id}`, data)
  },
  adminUpdateUserProfile(id, data) {
    return apiClient.patch(`/admin/users/${id}/profile`, data)
  },
  sendUserInvitation(id) {
    return apiClient.post(`/admin/users/${id}/invite`)
  },
  addUserToOrganization(userId, organizationId, role) {
    return apiClient.post(`/admin/users/${userId}/organizations`, {
      organization_id: organizationId,
      role,
    })
  },
  updateUserOrganizationRole(userId, organizationId, role) {
    return apiClient.patch(`/admin/users/${userId}/organizations/${organizationId}`, { role })
  },
  removeUserFromOrganization(userId, organizationId) {
    return apiClient.delete(`/admin/users/${userId}/organizations/${organizationId}`)
  },

  // Admin endpoints
  getAdminStats() {
    return apiClient.get("/admin/stats")
  },
  getAdminActivity() {
    return apiClient.get("/admin/activity")
  },
  getAdminPlan(token) {
    return apiClient.get(`/admin/plans/${token}`)
  },
  checkPlanProgression(token) {
    return apiClient.get(`/admin/plans/${token}/check-progression`)
  },
  archivePlan(token) {
    return apiClient.delete(`/admin/plans/${token}/archive`)
  },
  changePlanOrganization(token, organizationId) {
    return apiClient.patch(`/admin/plans/${token}/organization`, {
      organization_id: organizationId,
    })
  },
  changeUserOrganization(userId, organizationId) {
    return apiClient.patch(`/admin/users/${userId}/organization`, {
      organization_id: organizationId,
    })
  },

  getOrganizations() {
    return apiClient.get("/organization")
  },
  getOrganizationBySlug(slug) {
    return apiClient.get(`/organization/${slug}`)
  },

  // Admin organization endpoints
  getAdminOrganizations(params) {
    return apiClient.get("/admin/organizations", { params })
  },
  getAdminOrganization(id) {
    return apiClient.get(`/admin/organizations/${id}`)
  },
  createOrganization(data) {
    return apiClient.post("/admin/organizations", data)
  },
  updateOrganization(id, data) {
    return apiClient.patch(`/admin/organizations/${id}`, data)
  },
  deleteOrganization(id) {
    return apiClient.delete(`/admin/organizations/${id}`)
  },
  requestMagicLink(email) {
    return apiClient.post("/auth/magic-link/request", { email })
  },
  verifyMagicLink(token, data = {}) {
    return apiClient.post("/auth/magic-link/verify", { token, ...data })
  },
  requestPhoneLink(phone) {
    return apiClient.post("/auth/phone/link/request", { phone })
  },
  verifyPhoneLink(code) {
    return apiClient.post("/auth/phone/link/verify", { code })
  },
  requestPhoneLogin(phone) {
    return apiClient.post("/auth/phone/login/request", { phone })
  },
  verifyPhoneLogin(phone, code) {
    return apiClient.post("/auth/phone/login/verify", { phone, code })
  },
  getConfig() {
    return apiClient.get("/config")
  },
  getResources() {
    return apiClient.get("/resources")
  },
  getVersion() {
    return apiClient.get("/version")
  },

  // Public FAQ
  getFAQs() {
    return apiClient.get("/faqs")
  },

  // Admin FAQ
  getAdminFAQs() {
    return apiClient.get("/admin/faqs")
  },
  createFAQ(data) {
    return apiClient.post("/admin/faqs", data)
  },
  updateFAQ(id, data) {
    return apiClient.put("/admin/faqs/" + id, data)
  },
  deleteFAQ(id) {
    return apiClient.delete("/admin/faqs/" + id)
  },
}
